function getSlotTimestamp(slot) {
  if (!slot || typeof slot.start_time !== "string") {
    return null;
  }

  const timestamp = Date.parse(slot.start_time);
  return Number.isNaN(timestamp) ? null : timestamp;
}

const DEFAULT_BUSINESS_TIMEZONE = "Europe/Rome";

function getDateParts(value, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function getSlotDateKey(slot, timeZone) {
  if (!slot || typeof slot.start_time !== "string") {
    return null;
  }

  const timestamp = getSlotTimestamp(slot);
  return timestamp === null ? null : getDateParts(new Date(timestamp), timeZone);
}

export function groupConsecutiveStartSlots(
  slots,
  slotStepMinutes = 5,
  timeZone = DEFAULT_BUSINESS_TIMEZONE
) {
  const expectedGapMs = slotStepMinutes * 60 * 1000;
  const validSlots = (Array.isArray(slots) ? slots : [])
    .map((slot) => ({ slot, timestamp: getSlotTimestamp(slot), dateKey: getSlotDateKey(slot, timeZone) }))
    .filter((item) => item.timestamp !== null && item.dateKey !== null)
    .sort((left, right) => left.timestamp - right.timestamp);

  return validSlots.reduce((groups, item) => {
    const currentGroup = groups[groups.length - 1];
    const previousSlot = currentGroup?.slots[currentGroup.slots.length - 1];
    const previousTimestamp = getSlotTimestamp(previousSlot);
    const previousDateKey = getSlotDateKey(previousSlot, timeZone);

    if (
      currentGroup &&
      previousTimestamp !== null &&
      previousDateKey === item.dateKey &&
      item.timestamp - previousTimestamp === expectedGapMs
    ) {
      currentGroup.slots.push(item.slot);
      return groups;
    }

    groups.push({ id: item.slot.start_time, slots: [item.slot] });
    return groups;
  }, []);
}

export function formatSlotTime(slotStartTime, timeZone = DEFAULT_BUSINESS_TIMEZONE) {
  const timestamp = Date.parse(slotStartTime);
  if (Number.isNaN(timestamp)) {
    return String(slotStartTime || "");
  }

  return new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

export function formatSlotDateTime(slotStartTime, timeZone = DEFAULT_BUSINESS_TIMEZONE) {
  const timestamp = Date.parse(slotStartTime);
  if (Number.isNaN(timestamp)) return String(slotStartTime || "");
  return new Intl.DateTimeFormat("it-IT", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

export function formatStartTimeGroup(group, timeZone = DEFAULT_BUSINESS_TIMEZONE) {
  const groupSlots = Array.isArray(group?.slots) ? group.slots : [];
  if (groupSlots.length === 0) {
    return "";
  }

  const firstTime = formatSlotTime(groupSlots[0].start_time, timeZone);
  if (groupSlots.length === 1) {
    return `Disponibile alle ${firstTime}`;
  }

  const lastTime = formatSlotTime(groupSlots[groupSlots.length - 1].start_time, timeZone);
  return `Puoi iniziare dalle ${firstTime} alle ${lastTime}`;
}

export function getBusinessDateString(
  now = new Date(),
  timeZone = DEFAULT_BUSINESS_TIMEZONE
) {
  return getDateParts(now, timeZone);
}

export function filterFutureSlots(slots, now = new Date()) {
  const nowTimestamp = now instanceof Date ? now.getTime() : Date.parse(now);
  if (Number.isNaN(nowTimestamp)) return [];
  return (Array.isArray(slots) ? slots : []).filter((slot) => {
    const timestamp = getSlotTimestamp(slot);
    return timestamp !== null && timestamp > nowTimestamp;
  });
}

export function buildPublicAvailabilityQuery({ serviceId, staffId, date }) {
  const params = new URLSearchParams({
    service_id: serviceId,
    date,
    slot_step_minutes: "5",
  });
  if (staffId) {
    params.set("staff_id", staffId);
  }
  return params.toString();
}

export function resetStartTimeSelection(form) {
  return { ...form, start_time: "" };
}

export function selectSlotStartTime(form, slot) {
  return { ...form, start_time: slot?.start_time || "" };
}
